import type {Property} from "@/src/modules/properties/types";

import type {ShiftProperty} from "../types";

export type ShiftPropertyAssignmentType = "primary" | "secondary";

export type ShiftFormProperty = Pick<Property, "id" | "name"> & {
  assignment_type: ShiftPropertyAssignmentType;
  gps_coordinates?: string;
};

export function shiftPropertiesToFormProperties(
  properties: readonly ShiftProperty[],
): ShiftFormProperty[] {
  return properties.map((row) => ({
    id: row.property.id,
    name: row.property.name,
    assignment_type: row.assignment_type,
  }));
}

export function propertyToFormProperty(
  property: Pick<Property, "id" | "name"> & {gps_coordinates?: string},
  assignment_type: ShiftPropertyAssignmentType,
): ShiftFormProperty {
  return {
    id: property.id,
    name: property.name,
    assignment_type,
    ...(property.gps_coordinates ? {gps_coordinates: property.gps_coordinates} : {}),
  };
}

export function addFormProperty(
  rows: ShiftFormProperty[],
  property: Pick<Property, "id" | "name"> & {gps_coordinates?: string},
): ShiftFormProperty[] {
  if (rows.some((row) => row.id === property.id)) {
    return rows;
  }

  const assignment_type: ShiftPropertyAssignmentType =
    rows.length === 0 ? "primary" : "secondary";

  return [...rows, propertyToFormProperty(property, assignment_type)];
}

export function removeFormProperty(rows: ShiftFormProperty[], propertyId: number): ShiftFormProperty[] {
  const next = rows.filter((row) => row.id !== propertyId);
  if (next.length === 0) {
    return next;
  }

  if (next.some((row) => row.assignment_type === "primary")) {
    return next;
  }

  return next.map((row, index) =>
    index === 0 ? {...row, assignment_type: "primary" as const} : row,
  );
}

export function setFormPropertyPrimary(
  rows: ShiftFormProperty[],
  propertyId: number,
): ShiftFormProperty[] {
  return rows.map((row) => ({
    ...row,
    assignment_type: row.id === propertyId ? "primary" : "secondary",
  }));
}

/** Demote the current primary to secondary and promote the first other row. */
export function demoteFormPropertyPrimary(
  rows: ShiftFormProperty[],
  propertyId: number,
): ShiftFormProperty[] {
  const others = rows.filter((row) => row.id !== propertyId);
  if (others.length === 0) {
    return rows;
  }

  const nextPrimaryId = others[0]!.id;
  return rows.map((row) => {
    if (row.id === nextPrimaryId) {
      return {...row, assignment_type: "primary"};
    }
    return {...row, assignment_type: "secondary"};
  });
}

export function mergeComboboxProperties(
  previous: ShiftFormProperty[],
  selected: Pick<Property, "id" | "name">[],
): ShiftFormProperty[] {
  const selectedIds = new Set(selected.map((property) => property.id));
  let next = previous.filter((row) => selectedIds.has(row.id));

  for (const property of selected) {
    if (!next.some((row) => row.id === property.id)) {
      next = addFormProperty(next, property);
    }
  }

  if (next.length > 0 && !next.some((row) => row.assignment_type === "primary")) {
    return next.map((row, index) =>
      index === 0 ? {...row, assignment_type: "primary" as const} : row,
    );
  }

  return next;
}

export function getPrimaryFormProperty(
  rows: readonly ShiftFormProperty[],
): ShiftFormProperty | undefined {
  return rows.find((row) => row.assignment_type === "primary") ?? rows[0];
}

export function formPropertyChipLabel(row: ShiftFormProperty): string {
  return shiftPropertyBadgeLabel(row.name, row.assignment_type, {showRole: true});
}

export function shiftPropertyBadgeLabel(
  name: string,
  assignment_type?: ShiftPropertyAssignmentType,
  options?: {showRole?: boolean},
): string {
  if (!options?.showRole || !assignment_type) {
    return name;
  }

  return assignment_type === "primary" ? `${name} · Primary` : `${name} · Secondary`;
}

/** Primary first, then secondaries in their existing order. */
export function sortFormPropertiesForDisplay(
  rows: readonly ShiftFormProperty[],
): ShiftFormProperty[] {
  const primary = rows.filter((row) => row.assignment_type === "primary");
  const secondary = rows.filter((row) => row.assignment_type !== "primary");
  return [...primary, ...secondary];
}
